import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ViewChild,
    inject,
    ChangeDetectionStrategy,
} from '@angular/core';
import { OutputTaxService } from 'src/app/shared/services/output-tax/tax-output.service';
import { OutputTax } from 'src/app/shared/services/output-tax/interfaces/output-tax-request';
import {
    chunk as _chunk,
    map as _map,
    toString as _toString,
    isFinite as _isFinite,
    get as _get,
    sumBy as _sumBy,
} from 'lodash';
import { from } from 'rxjs';
import { concatMap, switchMap, toArray } from 'rxjs/operators';
import FileSaver from 'file-saver';
import { DxDataGridComponent, DxTooltipComponent } from 'devextreme-angular';
import {
    MessageType,
    NotificationService,
    PrintSheetService,
    RenderPersistReportService,
    UtilService,
} from 'src/app/shared/services';
import {
    OutputTaxItem,
    OutputTaxPrintSheetParameter,
} from 'src/app/shared/services/print-sheet/interfaces/output-tax-request';
import { monthSelection } from '../../models/constants';
import {
    toSellerPrintBlock,
    vatRegisteredEntityById,
} from 'src/app/shared/config/entity-config';
import { toTaxInvoicePageFromRow } from 'src/app/shared/services/tax-invoice-issue/build-tax-invoice';
import { FormatNumberPipe } from 'src/app/shared/pipes';
import moment from 'moment';
import { formatBuddhistEra } from 'src/app/shared/pipes/thai-datetime.pipe';
import { environment } from 'src/environments/environment';
import { on } from 'devextreme/events';
import { DxNumberBoxComponent } from 'devextreme-angular/ui/number-box';
import { DxSelectBoxComponent } from 'devextreme-angular/ui/select-box';
import { DxButtonComponent } from 'devextreme-angular/ui/button';
import { DxSwitchComponent } from 'devextreme-angular/ui/switch';
import { DxBoxComponent } from 'devextreme-angular/ui/box';
import {
    DxiItemComponent,
    DxiColumnComponent,
    DxiButtonComponent,
    DxoSummaryComponent,
    DxiTotalItemComponent,
} from 'devextreme-angular/ui/nested';
import { DxTextBoxComponent } from 'devextreme-angular/ui/text-box';
import { DxDataGridComponent as DxDataGridComponent_1 } from 'devextreme-angular/ui/data-grid';
import { DxTemplateDirective } from 'devextreme-angular/core';
import { DxTooltipComponent as DxTooltipComponent_1 } from 'devextreme-angular/ui/tooltip';
import { FormatNumberPipe as FormatNumberPipe_1 } from '../../../../shared/pipes/format-number.pipe';
@Component({
    selector: 'app-output-tax-summary',
    templateUrl: './output-tax-summary.component.html',
    styleUrls: ['./output-tax-summary.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DxNumberBoxComponent,
        DxSelectBoxComponent,
        DxButtonComponent,
        DxSwitchComponent,
        DxBoxComponent,
        DxiItemComponent,
        DxTextBoxComponent,
        DxDataGridComponent_1,
        DxiColumnComponent,
        DxiButtonComponent,
        DxTemplateDirective,
        DxoSummaryComponent,
        DxiTotalItemComponent,
        DxTooltipComponent_1,
        FormatNumberPipe_1,
    ],
})
export class OutputTaxSummaryComponent implements AfterViewInit {
    private cdref = inject(ChangeDetectorRef);
    private outputTaxService = inject(OutputTaxService);
    private formatNumberPipe = inject(FormatNumberPipe);
    private utilService = inject(UtilService);
    private notificationService = inject(NotificationService);
    private printSheetService = inject(PrintSheetService);
    private renderPersistReportService = inject(RenderPersistReportService);

    @ViewChild('dataGrid', { static: true }) dataGrid!: DxDataGridComponent;
    @ViewChild('sharedLinkButtonTooltip')
    sharedLinkButtonTooltip!: DxTooltipComponent;

    // Pages per render call in the month-end สำเนา batch — keeps a full
    // month to a handful of tabs without one oversized request.
    private static readonly COPY_BATCH_PAGES = 100;

    // The year editor's bounds, also the limits of the chevron stepping.
    readonly minYear = 2020;
    readonly maxYear = 2034;

    // The report is one taxpayer's report. Until the entity selector lands
    // (spec W10), this page reports genlogic-co only — exactly what it did
    // when the header read the module-level constant.
    private readonly reportEntity = vatRegisteredEntityById('genlogic-co');
    private readonly seller = toSellerPrintBlock(this.reportEntity);

    monthSelection = monthSelection;
    sharedLinkHover!: string;
    saveReport!: boolean;
    selectedYear!: number;
    selectedMonth!: number;
    selectedMonthDisplay!: string;
    isLoading = false;
    isEditorsDirty = true;
    dataSource: OutputTax[] = [];
    shareData: { fileName: string; sharedUrl: string } | null = null;
    ngAfterViewInit(): void {
        this.fillInitialValues();
        this.loadDataSource();
        this.cdref.detectChanges();
    }

    fillInitialValues(): void {
        const now = new Date();
        this.selectedYear = now.getFullYear();
        this.selectedMonth = now.getMonth() + 1;
    }

    loadDataSource(): void {
        if (
            _isFinite(this.selectedYear) &&
            _isFinite(this.selectedMonth) &&
            !this.isLoading
        ) {
            this.beginLoading();
            // Scoped to the report's taxpayer: the page header, totals, Drive
            // folder and the month-end สำเนา batch are all genlogic-co's, so
            // the query must be too — otherwise the first disorn row (the
            // 1-Oct flip is a config edit, not a deploy) would sum another
            // taxpayer's VAT into this report.
            this.outputTaxService
                .getOutputTax(
                    this.selectedYear,
                    this.selectedMonth,
                    this.reportEntity.id,
                )
                .subscribe(
                    (response: OutputTax[]) => {
                        this.dataSource = response;
                        this.isEditorsDirty = false;
                        this.endLoading();
                    },
                    (error) => {
                        this.endLoading();
                        console.error(error);
                        this.notificationService.pushMessage({
                            message: this.utilService.errorToString(error),
                            messageType: MessageType.error,
                        });
                    },
                );
        }
    }

    onEditorValueChanged(): void {
        this.isEditorsDirty = true;
    }

    // The chevrons beside the editors: step one month (rolling the year) and
    // reload immediately — no separate แสดง press, that's their point.
    stepMonth(delta: number): void {
        if (this.isLoading) {
            return;
        }
        const index = this.selectedYear * 12 + (this.selectedMonth - 1) + delta;
        const year = Math.floor(index / 12);
        if (year < this.minYear || year > this.maxYear) {
            return;
        }
        this.selectedYear = year;
        this.selectedMonth = (index % 12) + 1;
        this.loadDataSource();
    }

    // ── Soft void (spec W4/D2) ─────────────────────────────────────────────
    // A voided invoice keeps its number and its row; it shows struck through
    // and contributes nothing to any total. Arrow properties because
    // DevExtreme calls these unbound.

    amtBeforeVatCellValue = (row: OutputTax): number | null =>
        row?.voidedAt ? null : (_get(row, 'orderInfo.amtBeforeVat') ?? null);

    totalVatCellValue = (row: OutputTax): number | null =>
        row?.voidedAt ? null : (_get(row, 'orderInfo.totalVat') ?? null);

    netPriceCellValue = (row: OutputTax): number | null =>
        row?.voidedAt ? null : (_get(row, 'orderInfo.netPrice') ?? null);

    // Money cells still show a voided row's amounts (struck through by the
    // row class); the calculated value is null there precisely so the sums
    // stay clean, so the display falls back to the stored figure.
    priceCellDisplay = (cellData: any): number | null =>
        cellData?.data?.voidedAt
            ? (_get(cellData.data, cellData.column?.dataField) ?? null)
            : (cellData?.value ?? null);

    // Voided rows are struck through in place of the old สถานะ column.
    onRowPrepared(e: any): void {
        if (e.rowType === 'data' && e.data?.voidedAt) {
            e.rowElement.classList.add('voided-row');
        }
    }

    // Void and hard delete live in this right-click menu ONLY — the buttons
    // cell holds just เปิดไฟล์ (a visible void button was tried and removed,
    // 2026-08-19). The HARD delete is offered on each day's last-numbered
    // row; the Lambda re-checks against the live counter inside the delete
    // transaction either way.
    onGridContextMenu = (e: any): void => {
        if (e.row?.rowType !== 'data') {
            return;
        }
        const row: OutputTax = e.row.data;
        const items: any[] = [];
        if (!row.voidedAt) {
            items.push({
                text: 'ยกเลิกใบกำกับภาษี (void)',
                icon: 'clearsquare',
                disabled: this.isLoading,
                onItemClick: () => this.promptVoid(row),
            });
        }
        if (this.isLastInvoiceRow(row)) {
            items.push({
                text: 'ลบถาวรและคืนเลข (ใบสุดท้ายของวัน)',
                icon: 'trash',
                disabled: this.isLoading,
                onItemClick: () => this.promptHardDelete(row),
            });
        }
        if (items.length > 0) {
            e.items = [...(e.items ?? []), ...items];
        }
    };

    // The hard delete is offered wherever it can possibly succeed: on the
    // row holding the HIGHEST number among the loaded rows of its entity AND
    // ITS DAY — genlogic-co's counter is per-day, so every day's last number
    // is independently deletable (voided rows included: a voided last number
    // is still the one the counter steps back over). Both series formats are
    // fixed-width, so lexical order is issue order. The Lambda holds the
    // real authority (the live counter): for a monthly series (the Lambda's
    // unused 'IND-' cadence) day-grouping would over-offer within the month,
    // and any wrong offer fails safe there with its "not the last number"
    // message.
    private isLastInvoiceRow(row: OutputTax): boolean {
        const invNum = _toString(_get(row, 'orderInfo.invNum') ?? '');
        if (!invNum) {
            return false;
        }
        const entityId = _get(row, 'entityId') ?? 'genlogic-co';
        const date = _get(row, 'orderInfo.date') ?? null;
        const latestOfDay = this.dataSource
            .filter(
                (r) =>
                    (_get(r, 'entityId') ?? 'genlogic-co') === entityId &&
                    (_get(r, 'orderInfo.date') ?? null) === date,
            )
            .map((r) => _toString(_get(r, 'orderInfo.invNum') ?? ''))
            .reduce((a, b) => (b > a ? b : a), '');
        return invNum === latestOfDay;
    }

    private promptHardDelete(row: OutputTax): void {
        const invNum = _toString(_get(row, 'orderInfo.invNum') ?? '');
        if (!invNum || this.isLoading) {
            return;
        }
        // Typing the number back IS the confirmation — this one really
        // deletes, and the number will be issued again.
        // eslint-disable-next-line no-alert
        const typed = window.prompt(
            `ลบถาวร ${invNum}?\n\nรายการจะถูกลบออกจากฐานข้อมูลจริง และเลขที่นี้จะถูกนำไปใช้กับใบถัดไปของวันนั้น\n(ลบได้เฉพาะใบสุดท้ายของแต่ละวัน — ใบก่อนหน้าให้ใช้ยกเลิก/void)\n\nพิมพ์เลขที่ใบกำกับภาษีเพื่อยืนยันการลบ:`,
        );
        if (typed === null) {
            return; // operator cancelled
        }
        if (typed.trim() !== invNum) {
            this.notificationService.pushMessage({
                message: 'เลขที่ไม่ตรง — ไม่ได้ลบ',
                messageType: MessageType.error,
            });
            return;
        }
        this.beginLoading();
        this.outputTaxService.deleteLastTaxInvoice(invNum).subscribe(
            (response) => {
                this.endLoading();
                const fileNote = response?.fileSharedLink
                    ? ' (ไฟล์ใน Drive ยังไม่ถูกลบ)'
                    : '';
                this.notificationService.pushMessage({
                    message: `${invNum} ถูกลบถาวรแล้ว เลขที่นี้จะถูกใช้กับใบถัดไป${fileNote}`,
                    messageType: MessageType.success,
                });
                this.loadDataSource();
            },
            (error) => {
                this.endLoading();
                console.error(error);
                this.notificationService.pushMessage({
                    message: this.utilService.errorToString(error),
                    messageType: MessageType.error,
                });
            },
        );
    }

    private promptVoid(row: OutputTax): void {
        // The grid rows are the Lambda's NESTED shape; _get traverses it
        // (and would read the flat key on a flat row all the same).
        const invNum = _toString(_get(row, 'orderInfo.invNum') ?? '');
        if (!invNum || this.isLoading) {
            return;
        }
        // eslint-disable-next-line no-alert
        const reason = window.prompt(
            `ยกเลิกใบกำกับภาษี ${invNum}?\n\nเลขที่จะถูกเก็บไว้และแสดงขีดฆ่าในตาราง (ไม่ลบข้อมูล)\nกรุณาระบุเหตุผลการยกเลิก:`,
        );
        if (reason === null) {
            return; // operator cancelled
        }
        if (!reason.trim()) {
            this.notificationService.pushMessage({
                message: 'ต้องระบุเหตุผลการยกเลิก',
                messageType: MessageType.error,
            });
            return;
        }
        this.beginLoading();
        this.outputTaxService.voidTaxInvoice(invNum, reason.trim()).subscribe(
            () => {
                this.endLoading();
                this.notificationService.pushMessage({
                    message: `${invNum} ถูกยกเลิกแล้ว (เลขที่ถูกเก็บไว้ในรายงาน)`,
                    messageType: MessageType.success,
                });
                this.loadDataSource();
            },
            (error) => {
                this.endLoading();
                console.error(error);
                this.notificationService.pushMessage({
                    message: this.utilService.errorToString(error),
                    messageType: MessageType.error,
                });
            },
        );
    }

    onOpenFileButtonClicked(e: any): void {
        const sharedLink: string = _get(e, 'row.data.fileSharedLink');
        if (sharedLink) {
            window.open(sharedLink, '_blank');
        } else {
            this.notificationService.pushMessage({
                message: 'ไม่มีข้อมูล',
                messageType: MessageType.error,
            });
        }
    }

    onShowReportClicked(): void {
        if (this.saveReport) {
            this.saveAndOpenReportLink();
        } else {
            this.previewReport();
        }
    }

    // Month-end สำเนา batch (spec W14, user decision): the parcel carried the
    // ต้นฉบับ only, so the printable copy of every issued invoice comes from
    // here — rebuilt from each row's OWN stored seller block and docType,
    // never the live registry, and marked สำเนา (เอกสารออกเป็นชุด). Voided
    // rows print no copy. Chunked so a full month renders as a handful of
    // tabs rather than one giant request.
    onPrintMonthCopiesClicked(): void {
        if (this.isLoading || this.isEditorsDirty) {
            return;
        }
        const rows = this.liveRows();
        if (rows.length === 0) {
            this.notificationService.pushMessage({
                message: 'ไม่มีใบกำกับภาษีในเดือนที่เลือก',
                messageType: MessageType.info,
            });
            return;
        }
        const pages = rows.map((row) =>
            toTaxInvoicePageFromRow(row, { isCopy: true }),
        );
        const chunks = _chunk(
            pages,
            OutputTaxSummaryComponent.COPY_BATCH_PAGES,
        );
        this.beginLoading();
        from(chunks)
            .pipe(
                concatMap((chunkPages, index) =>
                    this.printSheetService.printTaxInvoice(
                        chunkPages,
                        `สำเนา ${this.selectedYear}-${this.selectedMonth} (${
                            index + 1
                        }/${chunks.length})`,
                    ),
                ),
                toArray(),
            )
            .subscribe(
                () => {
                    this.endLoading();
                    this.notificationService.pushMessage({
                        message: `พิมพ์สำเนา ${pages.length} ฉบับ (${chunks.length} ชุด)`,
                        messageType: MessageType.success,
                    });
                },
                (error) => {
                    this.endLoading();
                    console.error(error);
                    this.notificationService.pushMessage({
                        message: this.utilService.errorToString(error),
                        messageType: MessageType.error,
                    });
                },
            );
    }

    // Download twin of the สำเนา batch above: the same rebuilt สำเนา pages in
    // the same chunks, but merged into ONE PDF file instead of opened as
    // tabs. Deliberately a re-render, not a pull of the Drive originals —
    // Drive blocks cross-origin fetches, and rows whose archive failed have
    // no file there to pull; rebuilt copies cover every row and carry the
    // สำเนา mark a reproduction must.
    onDownloadMonthCopiesClicked(): void {
        if (this.isLoading || this.isEditorsDirty) {
            return;
        }
        const rows = this.liveRows();
        if (rows.length === 0) {
            this.notificationService.pushMessage({
                message: 'ไม่มีใบกำกับภาษีในเดือนที่เลือก',
                messageType: MessageType.info,
            });
            return;
        }
        const pages = rows.map((row) =>
            toTaxInvoicePageFromRow(row, { isCopy: true }),
        );
        const chunks = _chunk(
            pages,
            OutputTaxSummaryComponent.COPY_BATCH_PAGES,
        );
        const fileName = `สำเนาใบกำกับภาษี ${this.selectedYear}-${this.selectedMonth}.pdf`;
        this.beginLoading();
        from(chunks)
            .pipe(
                concatMap((chunkPages) =>
                    this.printSheetService.renderTaxInvoiceBase64(chunkPages),
                ),
                toArray(),
                switchMap((base64Pdfs) =>
                    this.printSheetService.mergePdfBase64ToBlob(base64Pdfs),
                ),
            )
            .subscribe(
                (merged) => {
                    FileSaver.saveAs(merged.blob, fileName);
                    this.endLoading();
                    this.notificationService.pushMessage({
                        message: `ดาวน์โหลด ${fileName} (${pages.length} ฉบับ, ${merged.pageCount} หน้า)`,
                        messageType: MessageType.success,
                    });
                },
                (error) => {
                    this.endLoading();
                    console.error(error);
                    this.notificationService.pushMessage({
                        message: this.utilService.errorToString(error),
                        messageType: MessageType.error,
                    });
                },
            );
    }

    openSharedUrl(): void {
        const sharedUrl: string | undefined = _get(this.shareData, 'sharedUrl');
        if (sharedUrl) {
            window.open(sharedUrl, '_blank');
        }
    }

    copySharedUrlToClibboard(): void {
        const sharedUrl = _get(this.shareData, 'sharedUrl');
        if (sharedUrl) {
            navigator.clipboard.writeText(sharedUrl).then(
                () => {
                    this.notificationService.pushMessage({
                        message: 'URL copied to clipboard.',
                        messageType: MessageType.success,
                        displayTime: 100,
                    });
                },
                (e) => {
                    console.error(e);
                },
            );
        }
    }

    onCellPrepared(e: any) {
        if (e.rowType === 'data' && e.column.dataField === 'fileSharedLink') {
            // The cell holds exactly one button (เปิดไฟล์) — bind its anchor
            // alone. getElementsByTagName over the cell once attached this
            // tooltip to every button in it, void included.
            const linkElement = e.cellElement.querySelector('a');
            if (linkElement) {
                on(linkElement, 'mouseover', (arg: any) => {
                    this.sharedLinkHover = e.data.fileSharedLink;
                    this.sharedLinkButtonTooltip.instance.show(arg.target);
                });
                on(linkElement, 'mouseout', () => {
                    this.sharedLinkButtonTooltip.instance.hide();
                });
            }
        }
    }

    formatDateCell(cellInfo: any): string {
        const momentObj = moment(cellInfo.valueText, 'YYYYMMDD');
        if (momentObj.isValid()) {
            momentObj.locale('th');
            return momentObj.format('DD MMM YYYY');
        }
        return cellInfo.valueText;
    }

    private previewReport(): void {
        if (!this.isLoading) {
            const param = this.preparePrintSheetParam();
            this.beginLoading();
            this.printSheetService
                .printOutputTaxReport(param, this.constructReportName())
                .subscribe(
                    () => {
                        this.notificationService.pushMessage({
                            message: 'สร้างรายงานสำเร็จ',
                            messageType: MessageType.success,
                        });
                        this.endLoading();
                    },
                    (error) => {
                        console.error(error);
                        this.notificationService.pushMessage({
                            message: this.utilService.errorToString(error),
                            messageType: MessageType.error,
                        });
                        this.endLoading();
                    },
                );
        }
    }

    private saveAndOpenReportLink(): void {
        if (!this.isLoading) {
            const param = this.preparePrintSheetParam();
            this.beginLoading();
            this.renderPersistReportService
                .renderAndGetSharedUrl<OutputTaxPrintSheetParameter>({
                    renderRequest: {
                        template: {
                            name: environment.outputTaxReportTemplateName,
                        },
                        data: param,
                    },
                    reportStoreRequest: {
                        fileName: `${this.constructReportName()}.pdf`,
                        parents: [this.reportEntity.reportDriveFolderId],
                    },
                })
                .subscribe(
                    (response) => {
                        this.shareData = {
                            fileName: response.name,
                            sharedUrl: response.webViewLink,
                        };
                        this.notificationService.pushMessage({
                            message: `${response.name} shared URL is now available.`,
                            messageType: MessageType.success,
                        });
                        this.endLoading();
                    },
                    (error) => {
                        console.error(error);
                        this.notificationService.pushMessage({
                            message: this.utilService.errorToString(error),
                            messageType: MessageType.error,
                        });
                        this.endLoading();
                    },
                );
        }
    }

    private constructReportName(): string {
        return `รายงานภาษีขาย ${this.selectedYear}-${this.selectedMonth}`;
    }

    private preparePrintSheetParam(): OutputTaxPrintSheetParameter {
        const param: OutputTaxPrintSheetParameter = {
            docInfo: {
                title: 'รายงานภาษีขาย',
                taxMonth: this.selectedMonthDisplay,
                taxYear: _toString(this.selectedYear + 543),
                name: this.seller.name,
                branchName: this.seller.branch,
                taxId: this.seller.taxId,
                placeName: this.seller.name,
                sumPreTaxAmt: this.calculateTotalPreTaxStr(),
                sumVatAmt: this.calculateTotalVatStr(),
            },
            items: this.getPrintSheetItemParam(),
        };
        return param;
    }

    private getPrintSheetItemParam(): OutputTaxItem[] {
        return _map<OutputTax, OutputTaxItem>(
            this.dataSource,
            (outputTax: OutputTax) => {
                // A voided invoice still appears in the report — a missing
                // number is an audit question — but marked ยกเลิก in the
                // amount columns and excluded from the totals (spec §4.2).
                const voided = !!outputTax.voidedAt;
                return {
                    invNum: this.formatString(
                        _get(outputTax, 'orderInfo.invNum'),
                    ),
                    date: this.formatDisplayDate(
                        _get(outputTax, 'orderInfo.date'),
                        'DD/MM/YY',
                    ),
                    cusName: this.formatString(
                        _get(outputTax, 'customerInfo.name'),
                    ),
                    cusTaxId: this.formatString(
                        _get(outputTax, 'customerInfo.taxId'),
                    ),
                    cusBranch: this.formatString(
                        _get(outputTax, 'customerInfo.branch'),
                    ),
                    preTaxAmt: voided
                        ? 'ยกเลิก'
                        : this.formatDecimal(
                              _get(outputTax, 'orderInfo.amtBeforeVat'),
                              2,
                          ),
                    vatAmt: voided
                        ? 'ยกเลิก'
                        : this.formatDecimal(
                              _get(outputTax, 'orderInfo.totalVat'),
                              2,
                          ),
                };
            },
        );
    }

    private formatDisplayDate(input: number | null, format: string): string {
        if (input) {
            const momentObj = moment(_toString(input), 'YYYYMMDD');
            if (momentObj.isValid()) {
                return formatBuddhistEra(momentObj, format);
            }
        }
        return '';
    }

    private formatString(input: any): string {
        return _toString(input).trim();
    }

    // Voided rows are shown in the report but never summed.
    private liveRows(): OutputTax[] {
        return this.dataSource.filter((row) => !row.voidedAt);
    }

    private calculateTotalPreTaxStr(): string {
        const total: number = _sumBy(this.liveRows(), 'orderInfo.amtBeforeVat');
        return this.formatDecimal(total, 2);
    }

    private calculateTotalVatStr(): string {
        const total: number = _sumBy(this.liveRows(), 'orderInfo.totalVat');
        return this.formatDecimal(total, 2);
    }

    private formatDecimal(input: any, decimalPoint: number): string {
        const inputStr = _toString(input).trim();
        if (inputStr) {
            return this.formatNumberPipe.transform(inputStr, decimalPoint);
        }
        return inputStr;
    }

    private beginLoading(): void {
        this.isLoading = true;
        this.dataGrid.instance.beginCustomLoading('');
    }

    private endLoading(): void {
        this.isLoading = false;
        this.dataGrid.instance.endCustomLoading();
    }
}
